/**
 * WHERE "NEW X" ACTUALLY LIVES, in one table, keyed by the LIST route.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 *
 * The navigation rail wants a + next to the rows where a + means something. The
 * problem is that "create one" is not one mechanism in this app: some lists
 * hand you a `/new` route, some open a dialog from a button in the page header,
 * and some genuinely have no create at all because the record is a consequence
 * of another one. A rail that guesses gets two things wrong that are worse than
 * having no +: it links to a route that does not exist, and it advertises a
 * create to somebody the server will refuse.
 *
 * So the table is written from routes.tsx and the list pages themselves, not
 * from what the nouns suggest. Every non-null `createTo` below is a path that
 * the router really resolves (see dashboard/src/routes.tsx and the per-area
 * `index.tsx` route modules), and every `allow` is the capability THE PAGE
 * CHECKS TODAY, read off its own `useCan(...)` / `RequireRole allow={...}`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT `createTo: null` MEANS, AND WHAT IT DOES NOT
 *
 * It means a + cannot navigate. It does NOT mean the thing cannot be created.
 * Two different situations land on null and the comment on each entry says
 * which one it is:
 *
 *   · DIALOG. The create is a button in the page header that opens a modal in
 *     place (accounts, bank accounts, journal, warehouses, leave, users). There
 *     is no address to send anybody to, so the rail skips these for now. Giving
 *     them a URL is a page change, not a rail change.
 *   · NO CREATE. The record is made somewhere else entirely, or not by hand at
 *     all: a payment is recorded against an invoice or a bill, a stock movement
 *     is the trace a posted document leaves behind.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `allow: null` IS A FINDING, NOT AN OMISSION
 *
 * Five list pages show their New button to everybody who can open the list:
 * invoices, quotations, bills, contracts and payroll. There is no `useCan`
 * around those buttons, so there is nothing to read off, and this table refuses
 * to invent one. The capability that would be right already exists in
 * lib/permissions.ts (`invoices.write`, `quotations.write`, `bills.write`,
 * `contracts.write`, `payroll.write`) and the server already enforces exactly
 * that on POST, so a Viewer who clicks today reaches the form and is refused on
 * save. Fixing that belongs in the pages. Until it happens, a rail that wants
 * to be stricter than the page should read the capability from permissions.ts
 * itself rather than expecting to find it here.
 *
 * The `module` column is the optional product the row belongs to, copied from
 * the entry that already gates it in the sidebar or the settings rail. `null`
 * is core: it is always there.
 */

import type { Capability } from '@/lib/permissions';

export interface CreateTarget {
  /**
   * The route that creates one, exactly as a person reaches it, or null when
   * there is no address to send them to. Read the header comment before
   * treating null as "this cannot be created".
   */
  createTo: string | null;
  /**
   * The capability the LIST PAGE gates its own create control on today, or
   * null when it gates nothing.
   */
  allow: Capability | null;
  /** The optional product this list belongs to. null is core. */
  module: string | null;
}

export const CREATE_ROUTES = Object.freeze({
  /* ── sales ─────────────────────────────────────────────────────────────── */

  // Ungated in the page: see the header note on `allow: null`.
  '/invoices': Object.freeze({ createTo: '/invoices/new', allow: null, module: 'finance' }),
  '/quotations': Object.freeze({ createTo: '/quotations/new', allow: null, module: 'crm' }),
  '/customers': Object.freeze({
    createTo: '/customers/new',
    allow: 'customers.write',
    module: 'crm',
  }),
  '/contracts': Object.freeze({ createTo: '/contracts/new', allow: null, module: 'crm' }),
  /* No module key on purpose, matching the sidebar: the catalogue is what
     invoice lines are built from, so a company with Inventory switched off
     still sells "Site supervision, 40 hours" and still wants it saved. */
  '/items': Object.freeze({ createTo: '/items/new', allow: 'items.write', module: null }),

  /* ── inventory ─────────────────────────────────────────────────────────── */

  /* Stock levels are a VIEW of quantities, not a place records are born. The
     two buttons on that page send you to the warehouses page and to the
     adjustment form, both of which are their own rows below, so a + here would
     be a second door to somebody else's room. */
  '/inventory/stock': Object.freeze({ createTo: null, allow: null, module: 'inventory' }),
  /* A movement is the trace a posted document leaves. Nobody writes one: the
     page offers an export and nothing else. */
  '/inventory/movements': Object.freeze({ createTo: null, allow: null, module: 'inventory' }),
  '/inventory/adjustments': Object.freeze({
    createTo: '/inventory/adjustments/new',
    allow: 'stock.write',
    module: 'inventory',
  }),
  /* DIALOG. "New warehouse" opens a modal on the list; there is no /new route
     to point a + at. */
  '/inventory/warehouses': Object.freeze({
    createTo: null,
    allow: 'stock.write',
    module: 'inventory',
  }),

  /* ── purchases ─────────────────────────────────────────────────────────── */

  '/bills': Object.freeze({ createTo: '/bills/new', allow: null, module: 'finance' }),
  '/expenses': Object.freeze({
    createTo: '/expenses/new',
    allow: 'expenses.write',
    module: 'finance',
  }),
  '/vendors': Object.freeze({ createTo: '/vendors/new', allow: 'vendors.write', module: null }),

  /* ── money ─────────────────────────────────────────────────────────────── */

  /* NO CREATE, and this is the one people are most surprised by. A payment is
     always recorded against a document, from the invoice or the bill it
     settles, so the payments list is a register: it has no New button at all,
     and a + on it would open a form that has nothing to attach itself to. */
  '/payments': Object.freeze({ createTo: null, allow: null, module: 'finance' }),
  /* DIALOG. */
  '/accounting/bank-accounts': Object.freeze({
    createTo: null,
    allow: null,
    module: 'finance',
  }),
  '/subscriptions': Object.freeze({
    createTo: '/subscriptions/new',
    allow: 'subscriptions.write',
    module: null,
  }),

  /* ── accounting ────────────────────────────────────────────────────────── */

  /* Reports are read, never created. The only action on the landing page is a
     PDF of something that already exists. */
  '/reports': Object.freeze({ createTo: null, allow: null, module: null }),
  /* DIALOG. The create modal is also ungated in the page, like the five listed
     in the header note. */
  '/accounting/chart-of-accounts': Object.freeze({
    createTo: null,
    allow: null,
    module: 'finance',
  }),
  /* DIALOG, and `allow` is null for a second reason worth spelling out: posting
     a manual entry is gated in the page on an INLINE role test, Owner or
     Finance (note: not Admin), and there is no capability in permissions.ts
     that spells that pair. Naming a near-miss capability here would quietly
     widen or narrow it, so the field stays null and the page keeps its test. */
  '/accounting/journal': Object.freeze({ createTo: null, allow: null, module: 'finance' }),

  /* ── people ────────────────────────────────────────────────────────────── */

  '/employees': Object.freeze({
    createTo: '/employees/new',
    allow: 'employees.write',
    module: 'hr',
  }),
  /* DIALOG. "New request" opens a modal on the requests tab. */
  '/leave': Object.freeze({ createTo: null, allow: 'leave.write', module: 'hr' }),
  /* Tabs of /leave rather than rows of their own, and neither creates anything:
     a balance is computed and edited in place, and the week is a calendar. They
     are here so a lookup by pathname does not fall through to undefined. */
  '/leave/balances': Object.freeze({ createTo: null, allow: null, module: 'hr' }),
  '/leave/week': Object.freeze({ createTo: null, allow: null, module: 'hr' }),
  /* DIALOG, and deliberately no `allow`: the page can only ever show the person
     asking, so there is nothing a role could usefully gate. Same reasoning as
     the sidebar entry, which also carries no allow key. */
  '/my-leave': Object.freeze({ createTo: null, allow: null, module: 'hr' }),
  '/payroll': Object.freeze({ createTo: '/payroll/new', allow: null, module: 'hr' }),

  /* ── workspace ─────────────────────────────────────────────────────────── */

  /* NO CREATE for a file, and a DIALOG for a folder, which is why this is null
     rather than a path. A file arrives attached to a record it belongs to, and
     the only thing the files page itself makes is a folder, through a prompt in
     the header. A + reading "new file" would promise an upload this page does
     not have. */
  '/files': Object.freeze({ createTo: null, allow: null, module: null }),
  /* DIALOG. One invite, not two: the direct "new user" form was removed because
     it asked an admin to type somebody else's password. `users.manage` is read
     off the RequireRole around the whole route module, which is Owner; the
     button inside the page tests Owner or Admin, so the route is the narrower
     of the two and the one that decides. */
  '/users': Object.freeze({ createTo: null, allow: 'users.manage', module: null }),
  /* Read-only by nature: the log is written by everything else in the app. */
  '/activity': Object.freeze({ createTo: null, allow: null, module: null }),
  /* The agent writes its own memories. The page shows them and can open one;
     there is no "add" control to hang a + on. */
  '/memory': Object.freeze({ createTo: null, allow: null, module: 'ai' }),
} satisfies Record<string, CreateTarget>);

/** The list routes this table knows about. */
export type ListRoute = keyof typeof CREATE_ROUTES;

/**
 * The create destination for a list route, or null when the rail must not draw
 * a +. Unknown routes answer null too: a row that is not in the table is a row
 * nobody has checked, and guessing is the failure this file exists to prevent.
 */
export function createTargetFor(listRoute: string): CreateTarget | null {
  const table: Record<string, CreateTarget | undefined> = CREATE_ROUTES;
  return table[listRoute] ?? null;
}
